/**
 * The server's clock, as seen from this browser's `performance.now()`.
 *
 * Built from the ping the client already sends every few seconds: the `pong` carries the
 * server's `Date.now()`, and assuming the trip there took as long as the trip back, the
 * server read its clock halfway through the round trip. Only the fastest recent sample is
 * believed, because a slow one is a queue somewhere and a queue is never symmetric.
 *
 * It exists for the lights. The `countdown` message names an absolute start on the
 * server's clock, and converting that with the message's own `now` puts this client's
 * green light exactly one trip late — the whole of a player's one-way latency off their
 * race, on top of what the finish already cost them. See DECISIONS.md §59.
 */

/** Ping samples kept. At one per four seconds, the last half-minute. */
const WINDOW = 8;

interface Sample {
  rtt: number;
  /** Server ms minus local ms. */
  offset: number;
}

export class ClockSync {
  private readonly samples: Sample[] = [];

  /** One ping: sent and answered at these local times, stamped `serverNow` in between. */
  add(sentAt: number, receivedAt: number, serverNow: number): void {
    const rtt = receivedAt - sentAt;
    if (!Number.isFinite(rtt) || rtt < 0 || !Number.isFinite(serverNow)) return;
    this.samples.push({ rtt, offset: serverNow - (sentAt + rtt / 2) });
    if (this.samples.length > WINDOW) this.samples.shift();
  }

  /**
   * What the server's clock reads at the local `performance.now()` given, or null before the
   * first ping has come back. The trains run on this (see `RailTraffic`).
   */
  serverNow(localMs = performance.now()): number | null {
    if (this.samples.length === 0) return null;
    let best = this.samples[0];
    for (const s of this.samples) if (s.rtt < best.rtt) best = s;
    return localMs + best.offset;
  }

  /** The local `performance.now()` at which the server's clock reads `serverMs`. */
  toLocal(serverMs: number): number | null {
    if (this.samples.length === 0) return null;
    let best = this.samples[0];
    for (const s of this.samples) if (s.rtt < best.rtt) best = s;
    return serverMs - best.offset;
  }
}
